import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API_BASE_URL from '../config';

// Initial state
const initialState = {
  items: [],
  totalQuantity: 0,
  totalAmount: 0,
  loading: false,
  error: null,
};

// Helper function to calculate totals
const calculateTotals = (items) => {
  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const totalAmount = items.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 0)), 0);
  return { totalQuantity, totalAmount };
};

// Helper function to get auth token
const getAuthToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.error('No auth token found');
    return null;
  }
  return token;
};

// Async thunks
export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      if (!auth.isAuthenticated) {
        return { items: [], totalQuantity: 0, totalAmount: 0 };
      }
      
      const token = getAuthToken();
      if (!token) {
        return { items: [], totalQuantity: 0, totalAmount: 0 };
      }
      
      const response = await fetch(`${API_BASE_URL}/cart`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Fetch cart response:', data);
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch cart');
      }
      
      const items = data.data || [];
      const { totalQuantity, totalAmount } = calculateTotals(items);
      
      return { items, totalQuantity, totalAmount };
    } catch (error) {
      console.error('Fetch cart error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async (cartItem, { rejectWithValue, getState, dispatch }) => {
    try {
      const { auth } = getState();
      
      // Check authentication
      if (!auth.isAuthenticated) {
        console.error('User not authenticated');
        return rejectWithValue('Please login to add items to cart');
      }
      
      const token = getAuthToken();
      if (!token) {
        console.error('No token found');
        return rejectWithValue('Authentication token not found. Please login again.');
      }
      
      // Validate cart item
      if (!cartItem.product_id) {
        return rejectWithValue('Product ID is required');
      }
      
      // Prepare the request body - match exactly what backend expects
      const requestBody = {
        product_id: parseInt(cartItem.product_id),
        quantity: parseInt(cartItem.quantity) || 1,
        price: parseFloat(cartItem.price) || 0,
        variant_id: cartItem.variant_id ? parseInt(cartItem.variant_id) : null,
        variant_name: cartItem.variant_name || null,
        variant_color: cartItem.variant_color || null,
        size_id: cartItem.size_id ? parseInt(cartItem.size_id) : null,
        size_name: cartItem.size_name || null,
        size_details: cartItem.size_details || null,
        lens_id: cartItem.lens_id || null,
        lens_name: cartItem.lens_name || null,
        lens_price: parseFloat(cartItem.lens_price) || 0,
        lens_color_id: cartItem.lens_color_id ? parseInt(cartItem.lens_color_id) : null,
        lens_color_name: cartItem.lens_color_name || null,
        lens_color_code: cartItem.lens_color_code || null,
        addon_lenses: cartItem.addon_lenses || null,
        prescription: cartItem.prescription || null,
        image: cartItem.image || null
      };
      
      console.log('Adding to cart - Request:', {
        url: `${API_BASE_URL}/cart/add`,
        token: token.substring(0, 20) + '...', // Log partial token for debugging
        body: requestBody
      });
      
      const response = await fetch(`${API_BASE_URL}/cart/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      console.log('Response status:', response.status);
      
      // Parse response
      const data = await response.json();
      console.log('Add to cart response:', data);
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to add to cart');
      }
      
      // Successfully added, now fetch updated cart
      await dispatch(fetchCart());
      
      return { success: true, message: data.message };
    } catch (error) {
      console.error('Add to cart error:', error);
      return rejectWithValue(error.message || 'Error adding to cart');
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async ({ itemId, quantity }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { auth } = getState();
      if (!auth.isAuthenticated) {
        return rejectWithValue('Please login');
      }
      
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('Authentication token not found');
      }
      
      const response = await fetch(`${API_BASE_URL}/cart/update/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ quantity: parseInt(quantity) })
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to update cart');
      }
      
      await dispatch(fetchCart());
      
      return { success: true, message: data.message };
    } catch (error) {
      console.error('Update cart error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (itemId, { rejectWithValue, getState, dispatch }) => {
    try {
      const { auth } = getState();
      if (!auth.isAuthenticated) {
        return rejectWithValue('Please login');
      }
      
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('Authentication token not found');
      }
      
      const response = await fetch(`${API_BASE_URL}/cart/remove/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to remove from cart');
      }
      
      await dispatch(fetchCart());
      
      return { success: true, message: data.message };
    } catch (error) {
      console.error('Remove from cart error:', error);
      return rejectWithValue(error.message);
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearCart: (state) => {
      state.items = [];
      state.totalQuantity = 0;
      state.totalAmount = 0;
      state.error = null;
    },
    clearCartError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cart
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalQuantity = action.payload.totalQuantity;
        state.totalAmount = action.payload.totalAmount;
        state.error = null;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add to Cart
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCart, clearCartError } = cartSlice.actions;
export default cartSlice.reducer;